/**
 * Pure H OS · System-of-Record · Generic Create/Edit drawer.
 *
 * Uses only existing primitives (Drawer, TextInput, Button, Field). Renders
 * a field-by-field form driven by a `schema` list — no new visual language.
 * Persists via `api.create` / `api.patch` and mirrors the response into
 * the local live cache via `upsertLocal` so drawers reflect the new state
 * without a full bootstrap round-trip.
 */
import { useEffect, useState } from "react";
import { Drawer, Field, Segmented, Toggle } from "./lib.overlay";
import { TextInput, Button } from "./lib";
import { api } from "../services/api";
import { upsertLocal } from "../services/store";

export type FieldSpec =
  | { key: string; label: string; kind: "text";        placeholder?: string; hint?: string }
  | { key: string; label: string; kind: "number";      placeholder?: string; hint?: string }
  | { key: string; label: string; kind: "textarea";    placeholder?: string; hint?: string }
  | { key: string; label: string; kind: "select";      options: { value: string; label: string }[]; hint?: string }
  | { key: string; label: string; kind: "toggle";      hint?: string }
  | { key: string; label: string; kind: "multiselect"; options: { value: string; label: string }[]; hint?: string };

export function RecordEditor<T extends Record<string, unknown>>({
  open,
  onClose,
  mode,
  title,
  collection,
  schema,
  initial = {},
  onSaved,
  testId,
  sendForApprovalStatus,
}: {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  title: string;
  collection: string;
  schema: FieldSpec[];
  initial?: Partial<T>;
  onSaved?: (row: T) => void;
  testId?: string;
  /** When provided, exposes a "Send for approval" button that saves and
   *  transitions the record to the given status (e.g. "submitted" /
   *  "pending"). Reuses `api.transition` on the server so the audit trail
   *  captures the request. */
  sendForApprovalStatus?: string;
}) {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (open) {
      setValues({ ...(initial as Record<string, unknown>) });
      setErr(null);
      setComment("");
    }
    // Only re-seed values when the drawer OPENS. If parents pass a fresh
    // `initial` reference on every render (common with inline object
    // literals), including `initial` in the dep-list would wipe user
    // edits and can also cascade into infinite renders when the effect
    // triggers a state update that re-renders the parent that creates
    // a new `initial` reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function setField(k: string, v: unknown) {
    setValues((cur) => ({ ...cur, [k]: v }));
  }

  async function save() {
    setErr(null);
    setBusy(true);
    try {
      let row: Record<string, unknown>;
      if (mode === "create") {
        row = await api.create(collection, values, comment || undefined);
      } else {
        // Only patch fields that changed vs `initial`.
        const patch: Record<string, unknown> = {};
        for (const k of Object.keys(values)) {
          if ((initial as Record<string, unknown>)[k] !== values[k]) {
            patch[k] = values[k];
          }
        }
        if (Object.keys(patch).length === 0) {
          setErr("No changes to save.");
          setBusy(false);
          return;
        }
        const id = String((initial as Record<string, unknown>).id ?? "");
        row = await api.patch(collection, id, patch, comment || undefined);
      }
      upsertLocal(collection, row as { id?: string });
      onSaved?.(row as T);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function sendForApproval() {
    if (!sendForApprovalStatus) return;
    setErr(null); setBusy(true);
    try {
      // First persist any pending edits (create or patch) so the
      // approval request references the latest data.
      let row: Record<string, unknown>;
      if (mode === "create") {
        row = await api.create(collection, values, comment || undefined);
      } else {
        const id = String((initial as Record<string, unknown>).id ?? "");
        row = await api.patch(collection, id, values, comment || undefined);
      }
      const savedId = String(row.id ?? "");
      // Then transition the record — audit trail records "sent for approval".
      const promoted = await api.transition(collection, savedId, sendForApprovalStatus, comment || "Sent for approval");
      upsertLocal(collection, promoted as { id?: string });
      onSaved?.(promoted as T);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title={title} width={480} testId={testId}>
      <div data-testid={`${testId}-form`}>
        {schema.map((f) => (
          <Field key={f.key} label={f.label} hint={f.kind !== "select" && f.kind !== "multiselect" ? (f as any).hint : undefined}>
            {f.kind === "text" && (
              <TextInput
                value={String(values[f.key] ?? "")}
                onChange={(v) => setField(f.key, v)}
                placeholder={f.placeholder}
                testId={`${testId}-${f.key}`}
              />
            )}
            {f.kind === "number" && (
              <TextInput
                value={values[f.key] === undefined || values[f.key] === null ? "" : String(values[f.key])}
                onChange={(v) => setField(f.key, v === "" ? "" : Number(v))}
                placeholder={f.placeholder}
                type="number"
                testId={`${testId}-${f.key}`}
              />
            )}
            {f.kind === "textarea" && (
              <textarea
                data-testid={`${testId}-${f.key}`}
                value={String(values[f.key] ?? "")}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.placeholder}
                rows={3}
                className="w-full px-3 py-2 text-sm focus:outline-none"
                style={{
                  color: "var(--ph-ink-strong)",
                  background: "var(--ph-surface-card)",
                  border: "1px solid var(--ph-border-strong)",
                  borderRadius: "var(--ph-radius-sm)",
                  fontFamily: "var(--ph-body-font)",
                }}
              />
            )}
            {f.kind === "select" && (
              <Segmented<string>
                testId={`${testId}-${f.key}`}
                value={String(values[f.key] ?? f.options[0]?.value ?? "")}
                onChange={(v) => setField(f.key, v)}
                options={f.options}
              />
            )}
            {f.kind === "toggle" && (
              <Toggle
                checked={Boolean(values[f.key])}
                onChange={(v) => setField(f.key, v)}
                testId={`${testId}-${f.key}`}
                label={f.label}
              />
            )}
            {f.kind === "multiselect" && (
              <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-${f.key}`}>
                {f.options.map((opt) => {
                  const arr = (values[f.key] as string[]) || [];
                  const selected = arr.includes(opt.value);
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      data-testid={`${testId}-${f.key}-${opt.value}`}
                      onClick={() => {
                        const next = selected ? arr.filter((v) => v !== opt.value) : [...arr, opt.value];
                        setField(f.key, next);
                      }}
                      className="px-2 py-1 text-xs"
                      style={{
                        background: selected ? "var(--ph-brand-tint)" : "var(--ph-surface-card)",
                        color: selected ? "var(--ph-brand-primary)" : "var(--ph-ink-default)",
                        border: `1px solid ${selected ? "var(--ph-brand-primary)" : "var(--ph-border-strong)"}`,
                        borderRadius: "var(--ph-radius-sm)",
                        fontWeight: selected ? 600 : 400,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </Field>
        ))}
        <Field label="Note (audit trail)" hint="Optional context recorded on the audit event">
          <TextInput
            value={comment}
            onChange={setComment}
            placeholder="e.g. Manual issue for VIP customer"
            testId={`${testId}-comment`}
          />
        </Field>
        {err && (
          <div
            data-testid={`${testId}-error`}
            className="text-sm mt-3 p-3"
            style={{
              color: "var(--ph-danger)",
              background: "var(--ph-danger-tint)",
              borderRadius: "var(--ph-radius-sm)",
            }}
          >
            {err}
          </div>
        )}
        <div className="pt-4 flex justify-end gap-2 flex-wrap">
          <Button variant="secondary" onClick={onClose} testId={`${testId}-cancel`}>
            Cancel
          </Button>
          {sendForApprovalStatus && (
            <Button variant="secondary" onClick={sendForApproval} disabled={busy} testId={`${testId}-send-for-approval`}>
              {busy ? "Sending…" : "Send for approval"}
            </Button>
          )}
          <Button onClick={save} disabled={busy} testId={`${testId}-save`}>
            {busy ? "Saving…" : mode === "create" ? "Create record" : "Save changes"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
